/* Header V2 Interactivity — see docs/superpowers/specs/2026-03-15-header-v2-design.md */

/**
 * Manages flyout state for header-v2's two panels: 'menu' (mega menu)
 * and 'search' (predictive search). Only one panel can be open at a time.
 *
 * - Desktop mega menu opens on hover-intent (100ms delay).
 * - Search opens on click from either desktop or mobile triggers.
 * - Sticky header uses IntersectionObserver on .sticky-header-anchor.
 */
class HeaderV2 {
	static HOVER_DELAY_MS = 100;

	/** @param {HTMLElement} container */
	constructor(container) {
		this.container = container;
		this.flyout = container.querySelector('[data-flyout]');
		this.panels = container.querySelectorAll('[data-flyout-panel]');
		this.activePanel = null;
		this.hoverTimeout = null;
		this._isCapped = false;
		this._lastTrigger = null;

		this._bindDesktopMegaMenu();
		this._bindBurgerToggle();
		this._bindSearchTriggers();
		this._bindSearchClose();
		this._bindSearchPlaceholder();
		this._bindSearchResizeObserver();
		this._bindEscapeKey();
		this._bindStickyHeader();
		this._bindFlyoutTransitionEnd();
	}

	/**
	 * Opens the specified flyout panel, closing any other open panel first.
	 * @param {string} panelName — 'menu' or 'search'
	 * @param {HTMLElement|null} [triggerElement=null] — element that triggered the open (for focus return)
	 */
	open(panelName, triggerElement = null) {
		if (triggerElement) {
			this._lastTrigger = triggerElement;
		}
		this.panels.forEach((panel) => {
			const isTarget = panel.dataset.flyoutPanel === panelName;
			panel.classList.toggle('is-active', isTarget);
			panel.setAttribute('aria-hidden', String(!isTarget));
		});

		this.container.classList.add('header-v2--flyout-open');
		this.activePanel = panelName;
		// Kept after close so a compact menu flyout doesn't widen while its height animates shut.
		this.flyout.dataset.panel = panelName;

		const megaTrigger = this.container.querySelector('[data-flyout-trigger="menu"]');
		if (megaTrigger) {
			megaTrigger.setAttribute('aria-expanded', String(panelName === 'menu'));
		}

		this._fitFlyoutToActivePanel();
	}

	/** Closes all flyout panels and resets state. */
	close() {
		const hadFocus = this.flyout.contains(document.activeElement);
		this.flyout.style.overflowY = '';
		this.flyout.style.height = '0';

		this.panels.forEach((panel) => {
			panel.classList.remove('is-active');
			panel.setAttribute('aria-hidden', 'true');
		});

		this.container.classList.remove('header-v2--flyout-open');
		this.activePanel = null;

		const burger = this.container.querySelector('.header-v2__burger');
		if (burger) {
			burger.setAttribute('aria-expanded', 'false');
			burger.setAttribute('aria-label', 'Open menu');
		}

		const megaTrigger = this.container.querySelector('[data-flyout-trigger="menu"]');
		if (megaTrigger) {
			megaTrigger.setAttribute('aria-expanded', 'false');
		}

		// A hover-opened menu has no trigger to return to, but focus inside it shouldn't fall to the body.
		const returnFocusTo = this._lastTrigger ?? (hadFocus ? megaTrigger : null);
		if (returnFocusTo) {
			returnFocusTo.focus();
		}
		this._lastTrigger = null;
	}

	/**
	 * Toggles a flyout panel: closes it if already open, opens it otherwise.
	 * @param {string} panelName — 'menu' or 'search'
	 * @param {HTMLElement|null} [triggerElement=null] — element that triggered the toggle (for focus return)
	 */
	toggle(panelName, triggerElement = null) {
		if (this.activePanel === panelName) {
			this.close();
		} else {
			this.open(panelName, triggerElement);
		}
	}

	/** @private — sizes the flyout to the active panel, capped at the window below the nav bar. */
	_fitFlyoutToActivePanel() {
		const activePanel = this.flyout.querySelector('.header-v2__flyout-panel.is-active');
		const contentHeight = activePanel ? activePanel.scrollHeight : 0;
		const navBarHeight = this.container.querySelector('.header-v2__nav-bar').offsetHeight;
		const maxHeight = window.innerHeight - navBarHeight;
		this._isCapped = contentHeight > maxHeight;
		if (!this._isCapped) {
			this.flyout.style.overflowY = '';
		}
		this.flyout.style.height = Math.min(contentHeight, maxHeight) + 'px';
		// transitionend unlocks scrolling, but it never fires when the transition is off (reduced motion).
		if (this._isCapped && parseFloat(getComputedStyle(this.flyout).transitionDuration) === 0) {
			this.flyout.style.overflowY = 'auto';
		}
	}

	/* ---------- Private: Desktop Mega Menu Hover-Intent ---------- */

	/** @private */
	_bindDesktopMegaMenu() {
		const triggerLink = this.container.querySelector('[data-flyout-trigger="menu"]');
		if (!triggerLink) {
			return;
		}

		// Prevent navigation on click — toggle menu instead
		triggerLink.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggle('menu', triggerLink);
		});

		triggerLink.addEventListener('mouseenter', () => {
			this.hoverTimeout = setTimeout(() => this.open('menu'), HeaderV2.HOVER_DELAY_MS);
		});

		triggerLink.addEventListener('mouseleave', () => {
			clearTimeout(this.hoverTimeout);
		});

		// Close when cursor leaves the entire header container
		this.container.addEventListener('mouseleave', () => {
			if (this.activePanel === 'menu') {
				this.close();
			}
		});

		// Keep open when cursor enters the flyout area
		if (this.flyout) {
			this.flyout.addEventListener('mouseenter', () => {
				clearTimeout(this.hoverTimeout);
			});
		}
	}

	/* ---------- Private: Mobile Burger Toggle ---------- */

	/** @private */
	_bindBurgerToggle() {
		const burgerButton = this.container.querySelector('.header-v2__burger');
		if (!burgerButton) {
			return;
		}

		burgerButton.addEventListener('click', (e) => {
			e.preventDefault();
			this.toggle('menu', burgerButton);
			const isOpen = this.activePanel === 'menu';
			burgerButton.setAttribute('aria-expanded', String(isOpen));
			burgerButton.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
		});
	}

	/* ---------- Private: Search Flyout ---------- */

	/** @private */
	_bindSearchTriggers() {
		const FOCUS_DELAY_MS = 100;
		const triggers = this.container.querySelectorAll('[data-flyout-trigger="search"]');

		triggers.forEach((trigger) => {
			trigger.addEventListener('click', (e) => {
				e.preventDefault();
				this.toggle('search', trigger);

				if (this.activePanel === 'search') {
					const input = this.container.querySelector('#Search');
					if (input) {
						setTimeout(() => input.focus(), FOCUS_DELAY_MS);
					}
				}
			});
		});
	}

	/* ---------- Private: Search Close Button ---------- */

	/** @private */
	_bindSearchClose() {
		const closeBtn = this.container.querySelector('[data-search-close]');
		if (!closeBtn) {
			return;
		}

		closeBtn.addEventListener('click', (e) => {
			e.preventDefault();
			this.close();
		});
	}

	/* ---------- Private: Search Placeholder ---------- */

	/** @private */
	_bindSearchPlaceholder() {
		const input = this.container.querySelector('#Search');
		if (input) {
			input.placeholder = 'Search...';
		}
	}

	/* ---------- Private: Search Resize Observer ---------- */

	/** @private — recalculates flyout height when search results change */
	_bindSearchResizeObserver() {
		const searchPanel = this.flyout ? this.flyout.querySelector('[data-flyout-panel="search"]') : null;

		if (!searchPanel) {
			return;
		}

		const observer = new MutationObserver(() => {
			if (this.activePanel !== 'search') {
				return;
			}

			this._fitFlyoutToActivePanel();
		});

		observer.observe(searchPanel, { childList: true, subtree: true, attributes: true });
	}

	/* ---------- Private: Escape Key ---------- */

	/** @private */
	_bindEscapeKey() {
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape' && this.activePanel) {
				this.close();
			}
		});
	}

	/* ---------- Private: Sticky Header ---------- */

	/** @private */
	_bindStickyHeader() {
		const navBar = this.container.querySelector('.header-v2__nav-bar');
		if (navBar) {
			// The bar, not the container: an open flyout grows the container and would push stuck content down.
			publishStickyHeaderHeight(navBar);
		}

		const anchor = document.querySelector('.sticky-header-anchor');
		if (!anchor) {
			return;
		}

		const section = this.container.closest('.header-v2');
		if (!section) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				section.classList.toggle('sticky-header--stuck', !entry.isIntersecting);
			},
			{ threshold: 1.0 }
		);

		observer.observe(anchor);
	}

	/* ---------- Private: Flyout Transition End ---------- */

	/** @private */
	_bindFlyoutTransitionEnd() {
		if (!this.flyout) {
			return;
		}

		this.flyout.addEventListener('transitionend', (e) => {
			if (e.propertyName !== 'height') {
				return;
			}

			if (this.activePanel && this._isCapped) {
				this.flyout.style.overflowY = 'auto';
			}
		});
	}
}

/* ---------- Initialize ---------- */

document.addEventListener('DOMContentLoaded', () => {
	const container = document.querySelector('.header-v2__container');
	if (container) {
		new HeaderV2(container);
	}
});
